// src/utils/dateOfBirth.js
//
// One place for the date-of-birth rules the Settings page and the booking
// prompt both need, so they cannot disagree with each other or with the API
// (app/schemas/patient/settings.py: no future date, nobody over 120).
//
// The value is a plain "YYYY-MM-DD" string, which is what <input type="date">
// produces and what the API accepts. No timezone conversion happens anywhere:
// a date of birth is a calendar date, not an instant.

export const MAX_AGE_YEARS = 120;

/** Today as YYYY-MM-DD in the viewer's own calendar, for `max` on the input. */
export function todayISO(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** The earliest date the form accepts, for `min` on the input. */
export function earliestISO(now = new Date()) {
  return `${now.getFullYear() - MAX_AGE_YEARS}-01-01`;
}

/** "" when acceptable; otherwise a readable message. */
export function dateOfBirthError(value, { required = true, now = new Date() } = {}) {
  const text = String(value || "").trim();
  if (!text) return required ? "Enter your date of birth." : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "Enter a valid date of birth.";

  const [year, month, day] = text.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  const realDate =
    parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
  if (!realDate) return "Enter a valid date of birth.";

  if (text > todayISO(now)) return "Date of birth cannot be in the future.";
  if (year < now.getFullYear() - MAX_AGE_YEARS) return "Enter a valid date of birth.";
  return "";
}

/** Whole years completed today — the same rule the server applies. */
export function ageFrom(value, now = new Date()) {
  if (dateOfBirthError(value, { now })) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  let age = now.getFullYear() - year;
  if (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day)) age -= 1;
  return age;
}
